import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPublicBnbUnitById, getPublicPropertyById } from '../../api/services/propertyService';
import API_BASE_URL from '../../api/config/apiConfig';
import { apiClient } from '../../api/services/authService';

/**
 * ShareRedirect handles incoming "short" share links like /s/p/:id and /s/u/:id.
 * It resolves these IDs to their full property pages and redirects accordingly.
 * It uses multiple strategies to ensure both BnB and Rental units are resolved.
 */
const ShareRedirect = ({ type }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const resolveLink = async () => {
      if (!id) return;

      try {
        if (type === 'property') {
          // Direct redirect for property links
          if (!cancelled) {
            console.log('Redirecting to property:', id);
            navigate(`/property/${id}`, { replace: true });
          }
        } else if (type === 'unit') {
          console.log('Resolving unit link for ID:', id);
          let propertyId = null;

          // Strategy 1: Try public BnB unit endpoint
          try {
            const res = await getPublicBnbUnitById(id);
            const data = res?.data || res;
            const unitData = data?.data || data;
            propertyId = unitData?.propertyId || unitData?.propertyID || unitData?.property?.id;
            
            if (propertyId) console.log('Strategy 1 (BnB) Success. Property ID:', propertyId);
          } catch (e) {
            console.warn('Strategy 1 (BnB) failed or not a BnB unit.');
          }

          // Strategy 2: Try various "Rental Unit" resolution patterns
          if (!propertyId) {
            const potentialEndpoints = [
              `${API_BASE_URL}/api/public/rental/units/${id}`,
              `${API_BASE_URL}/api/public/properties/units/${id}`,
              `${API_BASE_URL}/api/public/properties/unit/${id}`,
              `${API_BASE_URL}/api/public/units/${id}`,
              `${API_BASE_URL}/api/share/unit/${id}`
            ];

            // Strategy 2a: Try pattern guessing
            for (const url of potentialEndpoints) {
              if (cancelled) break;
              try {
                console.log(`Trying resolution strategy: ${url}`);
                const res = await apiClient(url, { method: 'GET' }, false);
                if (res.ok) {
                  const data = await res.json();
                  const unitData = data?.data || data;
                  propertyId = unitData?.propertyId || unitData?.propertyID || unitData?.property?.id;
                  
                  if (propertyId) {
                    console.log(`Success using endpoint: ${url}. Property ID:`, propertyId);
                    break;
                  }
                }
              } catch (e) {}
            }

            // Strategy 2b: Search properties by unitId filter
            if (!propertyId && !cancelled) {
              try {
                const searchUrl = `${API_BASE_URL}/api/public/properties?unitId=${id}`;
                console.log(`Trying search strategy: ${searchUrl}`);
                const res = await apiClient(searchUrl, { method: 'GET' }, false);
                if (res.ok) {
                  const data = await res.json();
                  // Backends often return a page/list for search queries
                  const payload = data?.data || data;
                  const property = Array.isArray(payload?.content) ? payload.content[0] : (Array.isArray(payload) ? payload[0] : payload);
                  
                  if (property?.id) {
                    propertyId = property.id;
                    console.log('Strategy 2b (Search by unitId) Success. Property ID:', propertyId);
                  }
                }
              } catch (e) {
                console.warn('Strategy 2b failed.');
              }
            }
          }

          // Strategy 3: Try to fetch property details directly using the ID (fallback)
          if (!propertyId && !cancelled) {
            try {
              const res = await getPublicPropertyById(id);
              if (res) {
                propertyId = id;
                console.log('Strategy 3 (Direct Property) Success.');
              }
            } catch (e) {
              console.warn('Strategy 3 (Direct Property) failed.');
            }
          }

          if (propertyId && !cancelled) {
            navigate(`/property/${propertyId}?unitId=${id}`, { replace: true });
          } else if (!cancelled) {
            setError('This unit could not be resolved to a property. The link may have expired or the item is no longer available.');
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Share redirect resolution error:', err);
          setError('The shared link is invalid or the item is no longer available.');
        }
      }
    };

    resolveLink();

    return () => {
      cancelled = true;
    };
  }, [id, type, navigate]);

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        padding: '20px',
        textAlign: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <div style={{ fontSize: '64px', marginBottom: '20px' }}>⚠️</div>
        <h2 style={{ marginBottom: '12px' }}>Link Unavailable</h2>
        <p style={{ color: '#666', maxWidth: '400px', marginBottom: '24px' }}>{error}</p>
        <button 
          onClick={() => navigate('/')}
          style={{
            padding: '12px 24px',
            backgroundColor: '#0066ff',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          Go to Homepage
        </button>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '4px solid #f3f3f3',
        borderTop: '4px solid #0066ff',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: '16px'
      }}></div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <p style={{ color: '#666' }}>Redirecting to Tenant Score...</p>
    </div>
  );
};

export default ShareRedirect;
